import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FfrServer } from '@/types/ffr';
import { useOkruDownload } from '@/hooks/useOkruDownload';
import { useOkruSource } from '@/hooks/useOkruSource';
import { API_BASE } from '@/services/apiClient';

interface Props {
  server: FfrServer;
  titleId: string;
}

/**
 * SECURITY: this sandbox intentionally omits `allow-popups` and
 * `allow-top-navigation`, which blocks the ad networks' popunders and
 * full-page redirect-hijacks (verified against live DM/ok.ru/filemoon). Do NOT
 * add those tokens. `allow-same-origin` + `allow-scripts` are required for the
 * host player to run; in-iframe overlay/pre-roll ads are cross-origin DOM and
 * cannot be removed.
 */
const SANDBOX = 'allow-scripts allow-same-origin allow-forms allow-presentation';
const ALLOW = 'autoplay; fullscreen; encrypted-media; picture-in-picture';

/** True only for the real Dailymotion player origin. Anchored on the hostname
 *  (not a substring test) so a hostile frame like `https://dailymotion.evil.com`
 *  cannot spoof an end-event and drive the part selector. */
function isDailymotionOrigin(origin: string): boolean {
  try {
    const host = new URL(origin).hostname;
    return host === 'dailymotion.com' || host.endsWith('.dailymotion.com');
  } catch {
    return false;
  }
}

/** True when a cross-origin message from the Dailymotion player signals end-of-video. */
function isDailymotionEnd(ev: MessageEvent): boolean {
  if (!isDailymotionOrigin(ev.origin)) return false;
  const d = ev.data;
  if (typeof d === 'string') return /event=(video_end|videoend|ended)/.test(d);
  if (d && typeof d === 'object' && 'event' in d) {
    const e = String((d as { event: unknown }).event);
    return e === 'video_end' || e === 'videoend' || e === 'ended';
  }
  return false;
}

export function FfrPartPlayer({ server, titleId }: Props): JSX.Element {
  const { t } = useTranslation();
  const dl = useOkruDownload(titleId);
  const [activeIndex, setActiveIndex] = useState(1);
  const activeIndexRef = useRef(activeIndex);
  activeIndexRef.current = activeIndex;

  const active = server.parts.find((p) => p.index === activeIndex) ?? server.parts[0];

  // Native ad-free ok.ru MP4 for the active part (idle for DM/filemoon).
  const okruSource = useOkruSource(titleId, server.host, active.index);
  // A per-part <video> error (e.g. proxy 504 / expired signed URL) drops that
  // part to the ok.ru iframe. Reset when the part or server changes.
  const [videoErrored, setVideoErrored] = useState(false);
  useEffect(() => { setVideoErrored(false); }, [server.host, activeIndex]);

  const advance = (): void => {
    const next = active.index + 1;
    if (next <= server.parts.length) setActiveIndex(next);
  };

  const useNative = server.host === 'okru' && okruSource.status === 'ready' && okruSource.url !== null && !videoErrored;
  const okruResolving = server.host === 'okru' && !videoErrored
    && (okruSource.status === 'idle' || okruSource.status === 'loading');

  // Reset to part 1 whenever the server changes.
  useEffect(() => { setActiveIndex(1); }, [server.host]);

  // Clear a stale terminal download state (done/error) when the viewer moves to
  // a different part or server, so the button never claims "Downloaded" for a
  // part that was never fetched. Read the current kind via a ref (not a dep) so
  // this fires ONLY on part/server change — not when a download itself finishes
  // (which would flash "Downloaded" away) — and never interrupt an in-flight one.
  const dlReset = dl.reset;
  const dlKindRef = useRef(dl.state.kind);
  dlKindRef.current = dl.state.kind;
  useEffect(() => {
    if (dlKindRef.current !== 'downloading') dlReset();
  }, [server.host, activeIndex, dlReset]);

  // Dailymotion-only best-effort auto-advance. Manual "Part N" buttons are the
  // reliable path for every host (ok.ru/filemoon players emit no dependable end
  // event, so they are manual-only).
  useEffect(() => {
    if (server.host !== 'dailymotion') return;
    function onMessage(ev: MessageEvent): void {
      if (!isDailymotionEnd(ev)) return;
      const next = activeIndexRef.current + 1;
      if (next <= server.parts.length) setActiveIndex(next);
    }
    window.addEventListener('message', onMessage);
    return (): void => window.removeEventListener('message', onMessage);
  }, [server.host, server.parts.length]);

  const canDownload = server.host === 'okru' && server.downloadable;

  return (
    <div className="ffr-part-player">
      <div className="ffr-part-player__stage">
        {useNative && okruSource.url ? (
          <video
            key={`okru-native:${active.index}`}
            className="ffr-part-player__video"
            src={`${API_BASE}${okruSource.url}`}
            // CORS mode is REQUIRED: the proxy MP4 lives on our API origin
            // (api.cobstreams.com), a different origin from the page. Its
            // response carries Cross-Origin-Resource-Policy, which blocks a
            // plain (no-cors) cross-origin <video> with ERR_BLOCKED_BY_RESPONSE.
            // NotSameOrigin. A CORS request (crossOrigin) passes — the proxy
            // echoes Access-Control-Allow-Origin — and CORP is not enforced on a
            // CORS-passing response. Verified live: no-cors → MEDIA_ERR code 4,
            // crossOrigin="anonymous" → loadeddata OK.
            crossOrigin="anonymous"
            controls
            autoPlay
            playsInline
            onEnded={advance}
            onError={() => setVideoErrored(true)}
          />
        ) : okruResolving ? (
          <div className="ffr-part-player__loading" role="status">{t('common.loading')}</div>
        ) : (
          <iframe
            key={`${server.host}:${active.index}`}
            className="ffr-part-player__frame"
            title={t('ffr.playerTitle', { index: active.index })}
            src={active.embedUrl}
            sandbox={SANDBOX}
            allow={ALLOW}
            allowFullScreen
          />
        )}
      </div>
      {server.parts.length > 1 && (
        <div className="ffr-part-player__parts" role="group" aria-label={t('ffr.partsAria')}>
          {server.parts.map((p) => (
            <button
              key={p.index}
              type="button"
              aria-pressed={p.index === active.index}
              className={`ffr-part-player__part${p.index === active.index ? ' is-active' : ''}`}
              onClick={() => setActiveIndex(p.index)}
            >
              {t('ffr.part', { index: p.index })}
            </button>
          ))}
        </div>
      )}
      {canDownload && (
        <div className="ffr-part-player__actions">
          {dl.state.kind === 'downloading' ? (
            <>
              <span className="ffr-part-player__dl-status">{t('ffr.downloading')}</span>
              <button
                type="button"
                className="ffr-part-player__dl"
                onClick={() => dl.cancel()}
              >
                {t('ffr.downloadCancel')}
              </button>
            </>
          ) : (
            <button
              type="button"
              className="ffr-part-player__dl"
              onClick={() => void dl.start(active.index, `fullfightreplays-${titleId}-part${active.index}.mp4`)}
            >
              {dl.state.kind === 'done'
                ? t('ffr.downloadDone')
                : dl.state.kind === 'error'
                  ? t('ffr.downloadError')
                  : t('ffr.downloadPart', { index: active.index })}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
